import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ExternalLink, ClipboardPaste, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const pin = L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#61a229;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.45)"></div>' });

function Sync({ pos, onPick }) {
  const map = useMap();
  useEffect(() => { if (pos) map.setView(pos, Math.max(map.getZoom(), 17)); }, [pos?.[0], pos?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

const joinAddr = (r) => [r.street, r.housenumber].filter(Boolean).join(" ");

export function LocationPicker({ lat, lng, address, city, onChange }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const pos = lat != null && lng != null ? [lat, lng] : null;
  const openGoogle = () => {
    const q = [address, city].filter(Boolean).join(", ");
    const url = q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : pos ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : "https://www.google.com/maps";
    window.open(url, "_blank", "noopener");
  };
  const paste = async () => {
    try { const t = await navigator.clipboard.readText(); if (t) { setText(t); acquire(t); } } catch { toast.error("Incolla il link nel campo con tieni premuto → Incolla"); }
  };
  const acquire = async (value = text) => {
    if (!value.trim()) return toast.error("Incolla prima il link di Google Maps");
    setBusy(true);
    try {
      const { data } = await api.get("/public/geo/resolve", { params: { text: value } });
      const patch = { lat: data.lat, lng: data.lng, geo_source: "google_maps" };
      if (!address?.trim() && data.street) patch.address = joinAddr(data);
      if (!city?.trim() && data.city) patch.city = data.city;
      onChange(patch);
      setText("");
      toast.success(data.street ? `Posizione acquisita: ${joinAddr(data)}${data.city ? `, ${data.city}` : ""}` : "Posizione acquisita");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="location-picker">
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className="rounded-full" onClick={openGoogle} data-testid="open-google-maps-button">
          <ExternalLink className="h-4 w-4" /> Apri in Google Maps
        </Button>
        <div className="flex w-full gap-2 sm:w-auto sm:min-w-[240px] sm:flex-1">
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), acquire())}
            placeholder="Incolla link Google Maps o coordinate" data-testid="google-maps-link-input" />
          <Button type="button" variant="outline" size="icon" className="shrink-0 rounded-full" onClick={paste} title="Incolla" data-testid="paste-link-button"><ClipboardPaste className="h-4 w-4" /></Button>
          <Button type="button" className="shrink-0 rounded-full bg-[#61a229] hover:bg-[#4f8a1f]" onClick={() => acquire()} disabled={busy} data-testid="acquire-position-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Acquisisci
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">In Google Maps tocca il punto esatto, poi <b>Condividi → Copia link</b> e incollalo qui. Puoi anche toccare o trascinare il segnaposto sulla mappa.</p>
      <div className="relative z-0 h-56 overflow-hidden rounded-xl border" data-testid="location-map">
        <MapContainer center={pos || [41.9, 12.5]} zoom={pos ? 17 : 5} className="h-full w-full">
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
          {pos && <Marker position={pos} icon={pin} draggable
            eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); onChange({ lat: p.lat, lng: p.lng, geo_source: "mappa" }); } }} />}
          <Sync pos={pos} onPick={(p) => onChange({ ...p, geo_source: "mappa" })} />
        </MapContainer>
      </div>
      {pos && <p className="text-xs text-slate-600" data-testid="location-coords">{lat.toFixed(6)}, {lng.toFixed(6)}</p>}
    </div>
  );
}
